// Curated, premium-quality Decide tab questions for the 10 beta lessons.
// These bypass runtime AI generation so quality is deterministic and
// grounded in the exact Book Insight + Company Case shown immediately
// before the Decide stage.
//
// Authoring rules:
//  • Exactly 3 questions per lesson: book_insight, company_case, combined_application.
//  • One clearly correct answer, three plausible teen-realistic distractors.
//  • Answer choices balanced in length and tone — no length tells.
//  • Explanations connect back to the specific case/insight content.
//  • Plain language, no moralizing, ages 12–18.

import type { DecideQuestion } from "@/components/lesson/DecisionCheckStage";

export interface CuratedDecideCheck {
  minimum_pass_score: number;
  questions: DecideQuestion[];
}

export const BETA_DECIDE_QUESTIONS: Record<string, CuratedDecideCheck> = {
  // 1. Needs vs Wants
  "709b0a91-2103-4ddf-9a70-e4ee5dae0b41": {
    minimum_pass_score: 2,
    questions: [
      {
        question_id: "needs-q1",
        source_type: "book_insight",
        related_insight_section: "Book Insight: Your Money or Your Life",
        question_text:
          "Robin and Dominguez say a $100 purchase is really priced in something other than dollars. What is it?",
        answer_choices: [
          "The hours of your life you traded at work to earn that $100.",
          "The sales tax and shipping added on top of the sticker price.",
          "The difference between the regular price and the sale price.",
          "The interest you would owe if you bought it on a credit card.",
        ],
        correct_index: 0,
        correct_explanation:
          "Your Money or Your Life calls this 'life energy' — every dollar is hours of your life traded for money, which is the true cost behind the price tag.",
        incorrect_explanations: {
          "1": "Taxes and fees raise the sticker price, but the insight is about your life energy, not transaction costs.",
          "2": "Discount math doesn't change the real cost — the time you spent earning the money still went out the door.",
          "3": "Credit interest is one consequence, but the insight applies to every purchase, whether or not you borrowed.",
        },
      },
      {
        question_id: "needs-q2",
        source_type: "company_case",
        related_insight_section: "Company Case: Starbucks",
        question_text:
          "The Starbucks case showed how a $0.50 commodity becomes a $6 purchase. What is the company actually selling?",
        answer_choices: [
          "Better coffee beans than any competitor can source.",
          "An emotional experience that turns a want into a felt need.",
          "A discount large enough to justify the daily habit.",
          "A loyalty program that pays cash back on every drink.",
        ],
        correct_index: 1,
        correct_explanation:
          "The case is explicit: Starbucks profits by selling atmosphere, identity, and routine — the drink is secondary. That perception shift is what turns a want into a perceived need.",
        incorrect_explanations: {
          "0": "The case treats coffee as a $0.50 commodity. The product isn't what's premium — the experience is.",
          "2": "Starbucks doesn't compete on discounts. The premium price is the point.",
          "3": "Rewards exist, but they aren't what changes a want into a need — the experience and habit do.",
        },
      },
      {
        question_id: "needs-q3",
        source_type: "combined_application",
        related_insight_section: "Applied: Book Insight + Starbucks Case",
        question_text:
          "You spend $5/day at Starbucks. Using both the life-energy idea and the Starbucks case, what is the strongest move?",
        answer_choices: [
          "Keep buying it — daily small purchases never affect long-term wealth.",
          "Quit all wants forever, because any non-essential purchase is wasteful.",
          "Name it as a want, decide if the life-energy trade is worth it, then redirect some of it toward a goal.",
          "Switch to a cheaper coffee shop and stop thinking about it.",
        ],
        correct_index: 2,
        correct_explanation:
          "This combines both pieces: the Book Insight (life-energy trade) and the Starbucks case (perception turns wants into needs). Conscious choice — not avoidance and not autopilot — is the strongest decision.",
        incorrect_explanations: {
          "0": "$5/day is about $1,825/year. Invested at 8% for 20 years that's over $20,000 — small purchases do compound.",
          "1": "Both sources are about awareness, not deprivation. Wants can be worth it when chosen on purpose.",
          "3": "Switching shops doesn't address the real question — whether the trade of life energy is worth it at all.",
        },
      },
    ],
  },

  // 2. Delayed Gratification
  "e29e634d-e035-42fb-8568-a5241a02d595": {
    minimum_pass_score: 2,
    questions: [
      {
        question_id: "delay-q1",
        source_type: "book_insight",
        related_insight_section: "Book Insight: Atomic Habits",
        question_text:
          "Atomic Habits reframes delayed gratification. According to Clear, what do people who 'look disciplined' actually have?",
        answer_choices: [
          "Naturally higher willpower than most people.",
          "An environment designed so the patient choice is the easy default.",
          "Stricter rules that punish them for spending money.",
          "A higher income that makes saving feel automatic.",
        ],
        correct_index: 1,
        correct_explanation:
          "Clear's argument is that delayed gratification is a design problem, not a personality trait. They built systems — automatic transfers, friction on impulse buys — that make patience the path of least resistance.",
        incorrect_explanations: {
          "0": "Clear directly pushes back on this — willpower is unreliable; systems are what hold.",
          "2": "Punishment isn't the mechanism. Removing friction from good choices is.",
          "3": "Income helps, but the insight is about environment design, which works at any income.",
        },
      },
      {
        question_id: "delay-q2",
        source_type: "company_case",
        related_insight_section: "Company Case: Amazon",
        question_text:
          "Why did Amazon's nearly two decades of delayed profitability work out?",
        answer_choices: [
          "It refused to invest cash and just sat on its earnings.",
          "It got lucky with one breakout product nobody saw coming.",
          "It reinvested in infrastructure and AWS, so patient capital compounded into a much bigger business.",
          "It traded short-term profit for short-term marketing wins, not long-term assets.",
        ],
        correct_index: 2,
        correct_explanation:
          "The case shows Amazon plowing dollars back into logistics and AWS. Investors who held through the slow years were rewarded because the delayed reward compounded.",
        incorrect_explanations: {
          "0": "The opposite — Amazon spent aggressively, just not on dividends.",
          "1": "Amazon's growth wasn't a single lucky product. It was sustained reinvestment.",
          "3": "The case is specifically about long-term assets (infrastructure, AWS), not short-term wins.",
        },
      },
      {
        question_id: "delay-q3",
        source_type: "combined_application",
        related_insight_section: "Applied: Atomic Habits + Amazon",
        question_text:
          "You want to save half of every paycheck. Using Clear's idea and Amazon's playbook, what is the strongest move?",
        answer_choices: [
          "Promise yourself you'll save whatever is left at the end of the month.",
          "Auto-transfer the savings the moment money lands, and route it into something that compounds.",
          "Wait for a motivated week to set everything up manually.",
          "Spend first on things you want now, since the future is unpredictable anyway.",
        ],
        correct_index: 1,
        correct_explanation:
          "This combines both: design the environment (auto-transfer) so patience is the default, and put the saved money where it can compound — exactly Amazon's reinvestment pattern at personal scale.",
        incorrect_explanations: {
          "0": "'Whatever is left' is the classic Profit-First failure — usually nothing is left.",
          "2": "Relying on motivation is the trap Clear warns about. Systems beat willpower.",
          "3": "The Amazon case proves the opposite — patient capital outperforms impatient capital.",
        },
      },
    ],
  },

  // 3. Cash Flow
  "d2b87cb1-7774-4029-87e0-be51ab14840e": {
    minimum_pass_score: 2,
    questions: [
      {
        question_id: "cash-q1",
        source_type: "book_insight",
        related_insight_section: "Book Insight: Profit First",
        question_text:
          "Why does Profit First insist on paying yourself before paying expenses?",
        answer_choices: [
          "Because expenses always expand to use whatever money is available, so 'save what's left' usually leaves nothing.",
          "Because savings accounts pay more interest than checking accounts.",
          "Because paying expenses last is illegal for businesses.",
          "Because the IRS requires a fixed savings percentage from every paycheck.",
        ],
        correct_index: 0,
        correct_explanation:
          "Michalowicz's whole point is sequence: when you pay expenses first, spending fills the gap and nothing is left. Paying yourself first changes the order, not the size, of the flow.",
        incorrect_explanations: {
          "1": "Interest rates are not the mechanism Profit First describes.",
          "2": "There's no law about order of payment. It's a behavior pattern, not a legal one.",
          "3": "The IRS doesn't set personal savings rates. Profit First is a self-imposed rule.",
        },
      },
      {
        question_id: "cash-q2",
        source_type: "company_case",
        related_insight_section: "Company Case: Walmart",
        question_text:
          "How did Walmart use cash flow to become the largest company in the world?",
        answer_choices: [
          "By charging the highest markup in retail.",
          "By taking on huge debt to fund growth.",
          "By turning inventory faster than it had to pay suppliers, so cash kept moving in their favor.",
          "By eliminating all suppliers and producing every product itself.",
        ],
        correct_index: 2,
        correct_explanation:
          "The case is specifically about timing: inventory sells before supplier invoices come due, so the flow always works in Walmart's favor — flow beats size.",
        incorrect_explanations: {
          "0": "Walmart is famous for low markups, not high ones.",
          "1": "Debt wasn't the lever — operational cash flow timing was.",
          "3": "Walmart relies on a massive supplier network. The advantage is timing, not vertical control.",
        },
      },
      {
        question_id: "cash-q3",
        source_type: "combined_application",
        related_insight_section: "Applied: Profit First + Walmart",
        question_text:
          "Two friends both clear $200/month after bills. One spends it freely; the other auto-routes $150 to savings on payday. Using both sources, who is financially stronger?",
        answer_choices: [
          "They are equal because their incomes are equal.",
          "The first friend, because spending boosts the economy.",
          "The second friend, because they control the sequence of money and let it work for them before expenses can absorb it.",
          "The first friend, because flexible cash is always better than savings.",
        ],
        correct_index: 2,
        correct_explanation:
          "Both sources point to the same lesson: the sequence and timing of money matters more than the amount. The second friend uses Profit First sequencing, just like Walmart uses inventory timing.",
        incorrect_explanations: {
          "0": "Same income, different control of flow — the outcomes diverge fast.",
          "1": "This isn't a macroeconomics question; it's about who keeps more of their own cash.",
          "3": "Flexible cash that gets spent isn't an advantage — it's the problem Profit First was built to fix.",
        },
      },
    ],
  },

  // 4. Opportunity Cost
  "f1ec70b9-2619-41b4-b2e8-f89425bf4e1b": {
    minimum_pass_score: 2,
    questions: [
      {
        question_id: "opp-q1",
        source_type: "book_insight",
        related_insight_section: "Book Insight: Essentialism",
        question_text:
          "What does McKeown mean when he says every yes is also a no?",
        answer_choices: [
          "You should always say no to be safe with money.",
          "Every dollar or hour you commit eliminates the other things that same dollar or hour could have done.",
          "Saying no to one thing automatically makes you richer.",
          "Saying yes to many things is the fastest path to wealth.",
        ],
        correct_index: 1,
        correct_explanation:
          "Opportunity cost is the invisible side of every choice — McKeown's point is that committing a resource always means giving up the alternatives, whether or not you notice.",
        incorrect_explanations: {
          "0": "Essentialism is about choosing deliberately, not avoiding everything.",
          "2": "Saying no is only valuable when it protects the yes that matters.",
          "3": "Spreading yourself across many yeses is exactly the trap the book warns against.",
        },
      },
      {
        question_id: "opp-q2",
        source_type: "company_case",
        related_insight_section: "Company Case: Netflix",
        question_text:
          "Netflix walked away from a profitable DVD business to bet on streaming. What does the case show about opportunity cost?",
        answer_choices: [
          "Big opportunities usually require giving up something that already works.",
          "Companies should always keep every business line running, just in case.",
          "Streaming was an obvious, risk-free bet anyone would have made.",
          "DVDs failed on their own, so Netflix had no real choice to make.",
        ],
        correct_index: 0,
        correct_explanation:
          "The case is exactly this trade — staying with the profitable DVD business would have meant not having the cash, focus, and infrastructure to win streaming. Sacrificing the working thing was the cost of the bigger opportunity.",
        incorrect_explanations: {
          "1": "Keeping every line alive would have starved the streaming bet — that's the cost the case highlights.",
          "2": "At the time, streaming was risky and unproven. The decision was a real trade-off.",
          "3": "DVDs were still profitable when Netflix made the call. They chose to leave it.",
        },
      },
      {
        question_id: "opp-q3",
        source_type: "combined_application",
        related_insight_section: "Applied: Essentialism + Netflix",
        question_text:
          "You have $5,000. A vacation now, or invest it at 8% for 15 years (~$15,860). Using both sources, what's the strongest way to decide?",
        answer_choices: [
          "Take the vacation — life is short and the future is uncertain.",
          "Always invest, because spending money on experiences is wasteful.",
          "Name both the visible cost (the vacation) and the hidden cost (the $15,860), then pick the one that matches your highest priority.",
          "Split it evenly so you don't have to think about either side.",
        ],
        correct_index: 2,
        correct_explanation:
          "This combines both sources: surface the opportunity cost (Essentialism) and accept that any big move requires sacrificing a real alternative (Netflix). Conscious priority — not avoidance — is the answer.",
        incorrect_explanations: {
          "0": "That ignores the hidden $15,860 cost, which is the exact thing the lesson asks you to see.",
          "1": "Essentialism doesn't condemn enjoyment — it asks you to choose deliberately.",
          "3": "Splitting to avoid the choice is the default-mode trade-off the lesson warns against.",
        },
      },
    ],
  },

  // 5. Financial Tradeoffs
  "9979b6d9-b308-49f4-b9a5-29e5a6a7ff3f": {
    minimum_pass_score: 2,
    questions: [
      {
        question_id: "trade-q1",
        source_type: "book_insight",
        related_insight_section: "Book Insight: Essentialism",
        question_text:
          "Essentialism says trade-offs are not problems to avoid. What is McKeown's actual point?",
        answer_choices: [
          "You can maximize spending, saving, and investing all at once if you try hard enough.",
          "Trade-offs disappear once your income is high enough.",
          "Trade-offs are unavoidable — the only question is whether you make them consciously or by default.",
          "Smart people never have to choose between two financial goals.",
        ],
        correct_index: 2,
        correct_explanation:
          "McKeown reframes trade-offs as the cost of any real decision. You don't escape them by being clever; you handle them by being deliberate.",
        incorrect_explanations: {
          "0": "You can't simultaneously maximize everything — that's the exact illusion the book pushes back on.",
          "1": "Higher income changes the size of the trade-off, not whether it exists.",
          "3": "Every financial choice involves a trade. Pretending otherwise is the trap.",
        },
      },
      {
        question_id: "trade-q2",
        source_type: "company_case",
        related_insight_section: "Company Case: Costco",
        question_text:
          "Costco caps markups at 14% while competitors charge 25–50%. What trade-off is Costco making?",
        answer_choices: [
          "Higher product profit in exchange for lower membership loyalty.",
          "Lower product profit in exchange for stronger membership loyalty and repeat revenue.",
          "No trade-off — they just have lower costs than everyone else.",
          "Lower quality in exchange for higher volume.",
        ],
        correct_index: 1,
        correct_explanation:
          "The case is explicit: Costco gives up margin on each item to win loyalty and recurring membership revenue. That's a deliberate trade, not an accident.",
        incorrect_explanations: {
          "0": "Backwards — Costco trades the product profit, not the loyalty.",
          "2": "Their costs aren't magic. The discipline is in choosing low margin on purpose.",
          "3": "Costco competes on value, not by cutting quality.",
        },
      },
      {
        question_id: "trade-q3",
        source_type: "combined_application",
        related_insight_section: "Applied: Essentialism + Costco",
        question_text:
          "You're choosing between $70K in a low-cost city with a 10-minute commute, or $100K in a high-cost city with a 90-minute commute. Using both sources, what's the strongest way to decide?",
        answer_choices: [
          "Take the $100K — higher salary always wins.",
          "Take the $70K — lower stress always wins.",
          "Pick based on whichever city has cheaper restaurants.",
          "Name the specific trade — money vs. time, savings rate vs. quality of life — then pick the one that matches what you value most right now.",
        ],
        correct_index: 3,
        correct_explanation:
          "This is exactly what Essentialism and the Costco case teach: surface the real trade, choose deliberately. There's no universal answer — there is a deliberate one.",
        incorrect_explanations: {
          "0": "Headline salary ignores cost of living, taxes, and 80 minutes a day of life.",
          "1": "Lower stress can be valuable, but assuming it 'always wins' is the same default thinking.",
          "2": "Restaurant prices are not the trade-off the lesson is about.",
        },
      },
    ],
  },

  // 7. Credit Score Basics
  "c6d62ce8-0b9e-40ff-b09d-64bcda1f6210": {
    minimum_pass_score: 2,
    questions: [
      {
        question_id: "credit-q1",
        source_type: "book_insight",
        related_insight_section: "Book Insight: I Will Teach You to Be Rich",
        question_text:
          "Sethi treats a credit score as an asset. What kind of 'return' does the asset pay?",
        answer_choices: [
          "Monthly interest paid directly into your bank account.",
          "Cash-back rewards on every swipe of any card.",
          "Lower rates and better access on big loans — savings that add up to thousands over a lifetime.",
          "A guaranteed bonus from the credit bureau every year.",
        ],
        correct_index: 2,
        correct_explanation:
          "Sethi's framing is that your score shows up as the price tag on your next mortgage, car loan, or rental — a high score literally lowers what life costs you.",
        incorrect_explanations: {
          "0": "Credit scores don't pay interest. They lower the interest you pay.",
          "1": "Rewards come from card products, not from the score itself.",
          "3": "There's no bureau bonus. The score is a pricing signal, not a payout.",
        },
      },
      {
        question_id: "credit-q2",
        source_type: "company_case",
        related_insight_section: "Company Case: Apple Card",
        question_text:
          "What did the Apple Card design prove about credit and decisions?",
        answer_choices: [
          "Cards with the lowest fees automatically improve your credit score.",
          "Showing daily interest and category spending in plain view helps people make better credit decisions.",
          "Card color matters more than terms.",
          "Only premium customers should ever get a credit card.",
        ],
        correct_index: 1,
        correct_explanation:
          "The case is specifically about transparency: real-time balances, daily interest, and clear categories make the cost of credit visible, which leads to better choices.",
        incorrect_explanations: {
          "0": "Low fees are nice but don't move your score by themselves.",
          "2": "Card aesthetics aren't the case study's point. The transparency in the app is.",
          "3": "The case is about clarity for everyone, not gatekeeping.",
        },
      },
      {
        question_id: "credit-q3",
        source_type: "combined_application",
        related_insight_section: "Applied: Sethi + Apple Card",
        question_text:
          "Your score is 680. Using both sources, what's the strongest first move?",
        answer_choices: [
          "Open six new cards today to raise your average credit limit.",
          "Get on top of on-time payments and pull utilization down — the two biggest, fastest score levers.",
          "Close all your existing cards so you can start clean.",
          "Pay only the minimum on every card to keep cash on hand.",
        ],
        correct_index: 1,
        correct_explanation:
          "This combines both sources: treat the score as a paying asset (Sethi), and use the transparency the Apple Card highlights to actually see and lower utilization while staying on time.",
        incorrect_explanations: {
          "0": "Six new applications drop your average account age and ding your score.",
          "2": "Closing cards usually lowers your available credit and raises utilization — both hurt the score.",
          "3": "Minimum payments keep balances high, which keeps utilization high — the next lesson covers this trap.",
        },
      },
    ],
  },

  // 8. Borrowing Money — no DB insight/case, anchored to the lesson concept directly
  "db6fdc3b-f16d-4fe5-a750-c65c2c8dc5a1": {
    minimum_pass_score: 2,
    questions: [
      {
        question_id: "borrow-q1",
        source_type: "book_insight",
        related_insight_section: "Lesson Principle",
        question_text:
          "The lesson says: borrow only what creates more value than it costs. What does that actually mean?",
        answer_choices: [
          "Borrow whenever the monthly payment fits your budget.",
          "Borrow only if the thing you're buying will grow your skills, income, or assets by more than the loan's total cost.",
          "Borrow only when interest rates feel low.",
          "Borrow whenever a friend says it's a good idea.",
        ],
        correct_index: 1,
        correct_explanation:
          "Productive debt outgrows its cost — tools, education, or assets that increase your earnings or net worth. Consumer debt usually does the opposite, no matter how low the rate.",
        incorrect_explanations: {
          "0": "A 'fits the budget' payment can still cost you years of interest on something that loses value.",
          "2": "Low rates can lure you into borrowing for things that still lose value faster than the rate saves.",
          "3": "Social pressure isn't a financial filter. The math has to clear.",
        },
      },
      {
        question_id: "borrow-q2",
        source_type: "company_case",
        related_insight_section: "Applied Scenario",
        question_text:
          "Two teens both borrow $5,000. One buys a used car for a weekend job that pays $400/month. The other buys clothes and concert tickets. Which is using borrowed money the way the lesson describes?",
        answer_choices: [
          "Neither — borrowing is always bad.",
          "Both — money is money.",
          "The teen with the car, because the borrowed money produces income that more than covers the cost.",
          "The teen with the clothes, because spending boosts confidence and that's an asset.",
        ],
        correct_index: 2,
        correct_explanation:
          "The car turns into earnings; the clothes and tickets don't. That's the exact line the lesson draws between productive and consumer borrowing.",
        incorrect_explanations: {
          "0": "Borrowing isn't always bad — the lesson specifically allows it when it creates more value than it costs.",
          "1": "How you use borrowed money is everything. Same dollars, very different outcomes.",
          "3": "Confidence isn't a cash flow. The lesson asks for measurable value, not feelings.",
        },
      },
      {
        question_id: "borrow-q3",
        source_type: "combined_application",
        related_insight_section: "Applied: Lesson Principle in Practice",
        question_text:
          "A 'buy now, pay later' app offers you a $300 jacket in 4 payments of $75 with 0% interest. What's the strongest way to think about it?",
        answer_choices: [
          "0% interest means it's free money — take it.",
          "Even at 0%, you're borrowing $300 for something that loses value the moment you wear it — same lesson, just hidden behind a friendlier word.",
          "Only do it if your friends are using the same app.",
          "Buy two jackets so the per-jacket cost feels lower.",
        ],
        correct_index: 1,
        correct_explanation:
          "0% doesn't change what the lesson teaches: it's still borrowing, and the jacket isn't creating value beyond the cost. The framing changed; the math didn't.",
        incorrect_explanations: {
          "0": "0% removes interest, not the obligation. Missed payments often trigger fees that erase the 0%.",
          "2": "Peer use doesn't change the financial logic.",
          "3": "Doubling a non-productive purchase doubles the obligation, not the value.",
        },
      },
    ],
  },

  // 9. Minimum Payments Trap
  "fcbdef95-7194-4921-8cb7-dfdfebda0ee0": {
    minimum_pass_score: 2,
    questions: [
      {
        question_id: "min-q1",
        source_type: "book_insight",
        related_insight_section: "Book Insight: The Total Money Makeover",
        question_text:
          "Ramsey calls minimum payments a trap disguised as kindness. Using his $5,000 / 22% APR example, what's actually happening when you pay the $100 minimum?",
        answer_choices: [
          "Most of the $100 goes to principal, so the balance drops quickly.",
          "About $92 goes to interest and only ~$8 reduces the principal.",
          "The entire $100 pays down the principal and interest is added later.",
          "The interest is waived as long as you make the minimum.",
        ],
        correct_index: 1,
        correct_explanation:
          "That's the exact number from the insight: at 22% on $5,000, almost all of the minimum is interest. The 'progress' is mostly an illusion the lender profits from.",
        incorrect_explanations: {
          "0": "It's the opposite — the principal barely moves.",
          "2": "Interest is charged daily and added every cycle, not waived.",
          "3": "Making the minimum keeps you out of late fees, not out of interest.",
        },
      },
      {
        question_id: "min-q2",
        source_type: "company_case",
        related_insight_section: "Company Case: Uber-Financed Vehicles",
        question_text:
          "Uber drivers who finance vehicles through partner programs often see their effective hourly rate collapse. What does the case prove?",
        answer_choices: [
          "Driving for Uber is always a bad job.",
          "Minimum payments on high-interest loans can quietly drain income and trap workers in debt.",
          "Gas prices are the main reason rideshare workers struggle.",
          "Insurance is the only real cost drivers have.",
        ],
        correct_index: 1,
        correct_explanation:
          "The case is specifically about how loan minimums on a high-APR vehicle loan eat into earnings until take-home drops below $12/hour — exactly the trap minimum payments are designed to create.",
        incorrect_explanations: {
          "0": "The case isn't a verdict on the job. It's about the financing structure attached to it.",
          "2": "Gas matters, but the case calls out the loan minimums as the silent drainer.",
          "3": "Insurance is real, but the loan minimums are what the case highlights.",
        },
      },
      {
        question_id: "min-q3",
        source_type: "combined_application",
        related_insight_section: "Applied: Ramsey + Uber Drivers",
        question_text:
          "You owe $4,000 at 22% APR with a $80 minimum. Using both sources, what's the strongest move?",
        answer_choices: [
          "Pay the $80 — it keeps the bank happy and is the safest path.",
          "Stop paying entirely so the bank renegotiates.",
          "Cut expenses and aim for $400/month so most of each payment hits principal and you escape years of interest.",
          "Open a new card and transfer the balance every month forever.",
        ],
        correct_index: 2,
        correct_explanation:
          "Both sources point the same way: minimums maximize the lender's revenue (Ramsey) and quietly drain your real income (Uber case). Paying well above the minimum flips the math back in your favor.",
        incorrect_explanations: {
          "0": "Safe-feeling and financially safe aren't the same — the $80 path leaves you in debt for decades.",
          "1": "Non-payment damages your score and triggers fees. It's not a renegotiation strategy.",
          "3": "Balance-transfer churn racks up fees and credit hits without solving the underlying balance.",
        },
      },
    ],
  },

  // 11. Urgency as a Red Flag
  "e1e29e49-0074-48f5-ba15-70374284867e": {
    minimum_pass_score: 2,
    questions: [
      {
        question_id: "urg-q1",
        source_type: "book_insight",
        related_insight_section: "Book Insight: Influence (Cialdini)",
        question_text:
          "Cialdini calls urgency one of the most powerful influence triggers. Why does it work on scam victims?",
        answer_choices: [
          "Because urgent messages are always true.",
          "Because urgency shrinks your decision window so you react before you can evaluate.",
          "Because banks legitimately send most warnings with tight deadlines.",
          "Because most people enjoy time pressure.",
        ],
        correct_index: 1,
        correct_explanation:
          "Cialdini's argument is mechanical: urgency removes the time needed for clear thinking. That's the whole point of the manipulation.",
        incorrect_explanations: {
          "0": "Urgency in a message says nothing about whether the message is true.",
          "2": "Legitimate institutions almost never demand action in minutes or hours.",
          "3": "Pressure isn't enjoyable — but it does push action, which is what scammers want.",
        },
      },
      {
        question_id: "urg-q2",
        source_type: "company_case",
        related_insight_section: "Applied Scenario: IRS Gift-Card Scam",
        question_text:
          "The IRS gift-card scam relies on threats of arrest if you don't pay immediately. What does that scenario prove?",
        answer_choices: [
          "Real government agencies sometimes demand gift cards.",
          "Legitimate organizations don't pressure people into rushed payments with threats — urgency itself is a red flag.",
          "If a caller knows your name, the request is probably real.",
          "Gift cards are how the IRS processes back-tax payments.",
        ],
        correct_index: 1,
        correct_explanation:
          "The case is built around the exact pattern the lesson teaches: real institutions don't compress your decision time or demand untraceable payment methods.",
        incorrect_explanations: {
          "0": "No government agency uses gift cards. Ever.",
          "2": "Public data makes names easy to find. Knowing your name is not proof of legitimacy.",
          "3": "The IRS uses mail and bank-traceable payment methods, not retail gift cards.",
        },
      },
      {
        question_id: "urg-q3",
        source_type: "combined_application",
        related_insight_section: "Applied: Cialdini + IRS Scam Case",
        question_text:
          "You get a text: 'Your bank account will be locked in 2 hours. Verify here.' It looks official. What is the strongest first move?",
        answer_choices: [
          "Click the link fast so the account doesn't lock.",
          "Reply to the text asking the bank to confirm it.",
          "Treat the urgency as the red flag itself: open the bank's official app or call the number on the back of your card to verify.",
          "Forward it to a friend and ask what they think.",
        ],
        correct_index: 2,
        correct_explanation:
          "This combines both sources: Cialdini says urgency is the manipulation, and the IRS case shows real institutions don't behave this way. Verify through a channel you control — not the one the message gave you.",
        incorrect_explanations: {
          "0": "Clicking the link is exactly what the scammer needs you to do before you think.",
          "1": "Replying confirms your number is active and stays in the scammer's loop.",
          "3": "Your friend has no more information than you do — verification has to go to the bank itself.",
        },
      },
    ],
  },

  // 12. Too-Good-To-Be-True Returns
  "846aa833-7dd2-4059-892a-6fbebf6a2c2e": {
    minimum_pass_score: 2,
    questions: [
      {
        question_id: "tgt-q1",
        source_type: "book_insight",
        related_insight_section: "Book Insight: Fooled by Randomness",
        question_text:
          "Taleb argues people overestimate their ability to spot real opportunities and underestimate randomness. Why does that matter for spotting investment scams?",
        answer_choices: [
          "Because if a few people made money, the opportunity must be real.",
          "Because if you can't explain how the returns are actually generated, you can't tell real performance from a fabricated illusion.",
          "Because every random outcome turns into a long-term trend.",
          "Because screenshots of earnings are usually verified.",
        ],
        correct_index: 1,
        correct_explanation:
          "Taleb's point is exactly this — without a real explanation of where the returns come from, you cannot distinguish skill, luck, or fraud. Scams thrive in that gap.",
        incorrect_explanations: {
          "0": "Early winners in Ponzi schemes are paid with later investors' money. Their wins prove nothing.",
          "2": "Randomness rarely produces long-term trends — that's the misread Taleb warns about.",
          "3": "Screenshots are trivially fakeable and are a classic scam prop.",
        },
      },
      {
        question_id: "tgt-q2",
        source_type: "company_case",
        related_insight_section: "Applied Scenario: Guaranteed-Return Crypto",
        question_text:
          "The case described a crypto product promising 15% daily returns with no risk. What is the single biggest red flag?",
        answer_choices: [
          "The product is offered by a startup, not a bank.",
          "It uses crypto instead of stocks.",
          "Returns that are both very high and 'guaranteed' with no risk — that combination is the hallmark of fraud.",
          "It only accepts new investors during certain hours.",
        ],
        correct_index: 2,
        correct_explanation:
          "The case is explicit: unrealistically high + guaranteed + no risk is the signature pattern of fraudulent schemes, regardless of the asset class.",
        incorrect_explanations: {
          "0": "Plenty of legitimate products come from startups. The promise is what's broken.",
          "1": "Asset type isn't the red flag. The combination of 'guaranteed' and 'high' is.",
          "2": "Limited hours can be a tactic, but they aren't the core fraud signal here.",
        },
      },
      {
        question_id: "tgt-q3",
        source_type: "combined_application",
        related_insight_section: "Applied: Taleb + Crypto Scam Case",
        question_text:
          "A friend shares an app promising 3% daily returns, 'zero risk,' and shows you their earnings screen. You have $2,000 saved. What's the strongest move?",
        answer_choices: [
          "Invest $200 to test it — the worst case is small.",
          "Invest the full $2,000 since your friend has proof.",
          "Wait a week to invest so you don't feel rushed.",
          "Don't invest at all — 3% daily and 'zero risk' is mathematically impossible, and the earnings screen is exactly the prop these schemes use.",
        ],
        correct_index: 3,
        correct_explanation:
          "Both sources line up: Taleb says if you can't explain the returns you can't trust them, and the case shows screenshots and 'guaranteed' yields are the exact bait. A small loss in a scam is still 100% lost.",
        incorrect_explanations: {
          "0": "3% daily compounded is ~$1.9M from $200 in a year. That's not a 'small test' — it's a math impossibility.",
          "1": "Your friend likely cashed out early or hasn't tried to. Their screen proves nothing about whether you can withdraw.",
          "2": "Waiting a week doesn't fix that the underlying claim is impossible.",
        },
      },
    ],
  },
};

export function getCuratedDecideCheck(lessonId: string): CuratedDecideCheck | null {
  return BETA_DECIDE_QUESTIONS[lessonId] ?? null;
}